import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import type { ReferenceBundle, ServiceConfig, Session, ServiceOffering, ServiceCatalog, Preset, FieldDef, AggMetadata, AvailableDayInfo } from '../domain/types'

// Reference data (topologia + catálogo) carregada uma vez no boot e exposta às telas via
// contexto — espelha o padrão do TermsContext. Os helpers replicam a API de domain/queues,
// mas ligados aos dados carregados (mock = estáticos; real = previsions×sessions+templates).
export interface ReferenceData {
  services: ServiceCatalog[]
  serviceConfigs: ServiceConfig[]
  sessions: Session[]
  offerings: ServiceOffering[]
  presets: Record<string, Preset>
  customerFields: FieldDef[]
  journeyFields: FieldDef[]
  availableDays: AvailableDayInfo[]
  serviceConfigById: (id: number) => ServiceConfig | undefined
  sessionsOf: (serviceConfigId: number) => Session[]
  offeringsForService: (serviceId: number) => ServiceOffering[]
  findOffering: (serviceId: number, serviceConfigId?: number, sessionId?: number) => ServiceOffering | undefined
  /** rótulo do modelo; null/desconhecido → "Em branco" */
  presetLabel: (key: string | null) => string
  /** valores dos journeyFields marcados como summary, na ordem, sem vazios */
  metaSummary: (meta: AggMetadata) => string[]
}

const ReferenceCtx = createContext<ReferenceData | null>(null)

function build(b: ReferenceBundle): ReferenceData {
  const cfgById = new Map(b.serviceConfigs.map((c) => [c.serviceConfigId, c]))
  const offeringsForService = (serviceId: number) => b.offerings.filter((o) => o.serviceId === serviceId)
  const journeyFields = b.journeyFields ?? []
  return {
    services: b.services,
    serviceConfigs: b.serviceConfigs,
    sessions: b.sessions,
    offerings: b.offerings,
    presets: b.presets,
    availableDays: b.availableDays,
    customerFields: b.customerFields ?? [],
    journeyFields,
    serviceConfigById: (id) => cfgById.get(id),
    sessionsOf: (scid) => b.sessions.filter((s) => s.serviceConfigId === scid),
    offeringsForService,
    findOffering: (serviceId, scid, sessionId) => {
      const all = offeringsForService(serviceId)
      if (scid == null || sessionId == null) {return all[0]}
      return all.find((o) => o.serviceConfigId === scid && o.sessionId === sessionId) ?? all[0]
    },
    presetLabel: (key) => (key == null ? 'Em branco' : b.presets[key]?.label ?? key),
    metaSummary: (meta) => journeyFields
      .filter((f) => f.summary)
      .map((f) => meta.fields[f.key])
      .filter((v): v is string => !!v && v.trim() !== ''),
  }
}

export function ReferenceProvider({ bundle, children }: { bundle: ReferenceBundle; children: ReactNode }) {
  const value = useMemo(() => build(bundle), [bundle])
  return <ReferenceCtx.Provider value={value}>{children}</ReferenceCtx.Provider>
}

export function useReference(): ReferenceData {
  const v = useContext(ReferenceCtx)
  if (!v) {throw new Error('useReference fora do ReferenceProvider')}
  return v
}
